// What the expense report page reads from the Application API (ADR-0035): the report by a Dimension's values and
// expense account, the entries behind one figure, and the Document an entry opens to. Amounts are paise.
import { call, type Fetch } from "../../http.js";

export interface ExpenseFigure {
  readonly value: string | null;
  readonly amount: number;
}

export interface ExpenseEntry {
  readonly posting: string;
  readonly date: string;
  readonly document: { readonly object: string; readonly id: string; readonly number: string | null };
  readonly account: string;
  readonly amount: number;
  readonly counterparty?: string;
}

export interface ExpenseRow {
  readonly account: string;
  readonly name: string;
  readonly amount: number;
  readonly byValue: readonly ExpenseFigure[];
}

export interface ExpenseGroup {
  readonly section: string;
  readonly group: string;
  readonly amount: number;
  readonly byValue: readonly ExpenseFigure[];
  readonly accounts: readonly ExpenseRow[];
}

/** `GET /api/reports/expenses`: rows of expense accounts under their groups, a column per value, and the totals. */
export interface ExpenseReport {
  readonly from: string;
  readonly to: string;
  readonly dimension: string;
  readonly label: string;
  readonly columns: readonly { readonly value: string | null; readonly name: string }[];
  readonly dimensions: readonly { readonly id: string; readonly label: string }[];
  readonly groups: readonly ExpenseGroup[];
  readonly byValue: readonly ExpenseFigure[];
  readonly total: number;
  readonly hidden?: readonly "payroll"[];
}

/** `GET /api/reports/expenses/entries`: the entries behind one figure, and their suppliers' names. */
export interface ExpenseEntries {
  readonly entries: readonly ExpenseEntry[];
  readonly parties: Readonly<Record<string, string>>;
  readonly hidden?: readonly "payroll"[];
}

/** `GET /api/reports/expenses/document`: a Document's fields, as the person's Roles show them. */
export interface ExpenseDocument {
  readonly object: string;
  readonly id: string;
  readonly fields: Readonly<Record<string, unknown>>;
}

/** The report asked for: a period, and a Dimension unless the Tenant's first will do. */
export interface ExpensesAsked {
  readonly from: string;
  readonly to: string;
  readonly dimension?: string | undefined;
}

/** A figure to open: an account's (a row's), a value's (a column's, `null` the unallocated), or both. */
export interface FigureAsked extends ExpensesAsked {
  readonly dimension: string;
  readonly account?: string | undefined;
  readonly value?: string | null | undefined;
}

export interface ExpensesApi {
  expenses(asked: ExpensesAsked): Promise<ExpenseReport>;
  entries(asked: FigureAsked): Promise<ExpenseEntries>;
  document(object: string, id: string): Promise<ExpenseDocument>;
}

const query = (asked: ExpensesAsked & { account?: string | undefined; value?: string | null | undefined }) =>
  new URLSearchParams({
    from: asked.from,
    to: asked.to,
    ...(asked.dimension === undefined ? {} : { dimension: asked.dimension }),
    ...(asked.account === undefined ? {} : { account: asked.account }),
    ...(asked.value === undefined ? {} : { value: asked.value ?? "unallocated" }),
  }).toString();

/** Where the report downloads as a sheet. */
export const sheetHref = (asked: ExpensesAsked) => `/api/reports/expenses/sheet?${query(asked)}`;

export function createExpensesApi(fetcher: Fetch = (input, init) => fetch(input, init)): ExpensesApi {
  return {
    expenses: (asked) => call<ExpenseReport>(fetcher, `/api/reports/expenses?${query(asked)}`),
    entries: (asked) => call<ExpenseEntries>(fetcher, `/api/reports/expenses/entries?${query(asked)}`),
    document: (object, id) =>
      call<ExpenseDocument>(
        fetcher,
        `/api/reports/expenses/document?${new URLSearchParams({ object, id }).toString()}`,
      ),
  };
}
